import React from 'react';
import { Button, Checkbox, Tooltip } from '@mui/material';
import { beautifyNumber } from '../../../Utils/FormatNumber';
import { SummaryTile, TONES, labelStyle, tileStyle, valueStyle } from './PaymentAmountsSummaryBar';

const round2 = (value) => Number((value || 0).toFixed(2));

const toNumber = (raw) => {
    const n = typeof raw === 'string' ? parseFloat(raw) : raw;
    return Number.isFinite(n) ? n : 0;
};

const LINE_TYPES = ['checks', 'creditCards', 'cash', 'bankTransactions', 'offsets'];

/** Marker used to recognise a partial-payment note already present in a description. */
export const PARTIAL_PAYMENT_NOTE_MARKER = 'תשלום חלקי';

/**
 * Note appended to the description of a partial receipt/payment so the document itself
 * says how much of the invoice it covers (e.g. "תשלום חלקי 200 מתוך 1,000").
 */
export const buildPartialPaymentNote = (paidNow, documentTotal) => {
    const paid = round2(toNumber(paidNow));
    const total = round2(toNumber(documentTotal));
    if (paid <= 0 || total <= 0) return '';
    return `${PARTIAL_PAYMENT_NOTE_MARKER} ${beautifyNumber(paid).trim()} מתוך ${beautifyNumber(total).trim()}`;
};

/**
 * Open balance of the source document: its total minus what earlier receipts/payments
 * already covered. That balance — not the document total — is what a new payment can cover.
 */
export const computeOpenBalance = (documentTotal, alreadyPaid) =>
    round2(Math.max(0, round2(toNumber(documentTotal)) - round2(Math.max(0, toNumber(alreadyPaid)))));

/**
 * Re-splits the payment lines so they add up to `target`, keeping their current proportions.
 * Used when the paid amount changes: a duplicated document arrives with the full amount on
 * its lines, and a partial payment has to leave them adding up to the smaller amount.
 *
 * Lines with no amount yet are left alone — there is nothing to scale, and the summary bar
 * already pre-fills a new line with what is missing.
 */
export const rescaleLineAmounts = (item, target) => {
    if (!item) return item;

    const rows = [];
    LINE_TYPES.forEach((type) => {
        (item[type] || []).forEach((row, idx) => {
            rows.push({ type, idx, amount: toNumber(row?.amount) });
        });
    });

    const currentTotal = round2(rows.reduce((sum, row) => sum + row.amount, 0));
    const nextTotal = round2(toNumber(target));
    if (rows.length === 0 || currentTotal <= 0 || nextTotal <= 0 || currentTotal === nextTotal) {
        return item;
    }

    const ratio = nextTotal / currentTotal;
    const next = { ...item };
    LINE_TYPES.forEach((type) => {
        if (Array.isArray(item[type])) next[type] = item[type].map((row) => ({ ...row }));
    });

    // The last line absorbs the rounding remainder so the lines still add up exactly.
    let allocated = 0;
    rows.forEach((row, i) => {
        const isLast = i === rows.length - 1;
        const scaled = isLast ? round2(nextTotal - allocated) : round2(row.amount * ratio);
        allocated = round2(allocated + scaled);
        next[row.type][row.idx].amount = scaled;

        const foreignAmount = toNumber(next[row.type][row.idx].foreignCurrencyAmount);
        if (foreignAmount) {
            next[row.type][row.idx].foreignCurrencyAmount = round2(foreignAmount * ratio);
        }
    });

    return next;
};

const PERCENT_SHORTCUTS = [25, 50, 75];

const AmountInput = ({ label, value, onChange, disabled }) => {
    const colors = TONES.neutral;
    const [draft, setDraft] = React.useState(null);

    return (
        <div
            style={{
                ...tileStyle(colors),
                borderStyle: 'dashed',
                borderColor: '#304FFF',
                cursor: disabled ? 'default' : 'text',
                opacity: disabled ? 0.6 : 1,
            }}
        >
            <span style={labelStyle(colors)}>{label}</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <input
                    type="number"
                    disabled={disabled}
                    value={draft ?? (value === '' || value == null ? '' : value)}
                    onChange={(e) => {
                        setDraft(e.target.value);
                        onChange(e.target.value === '' ? null : Number(e.target.value));
                    }}
                    onBlur={() => setDraft(null)}
                    style={{
                        ...valueStyle(colors),
                        width: '92px',
                        padding: 0,
                        border: 'none',
                        outline: 'none',
                        background: 'transparent',
                        textAlign: 'center',
                    }}
                />
                <span style={valueStyle(colors)}>₪</span>
            </div>
        </div>
    );
};

const shortcutButtonSx = {
    minWidth: '52px',
    padding: '4px 8px',
    fontSize: '12px',
    fontWeight: 600,
    borderRadius: '6px',
    border: '1px solid #304FFF',
    color: '#304FFF',
    background: '#FFF',
    '&:hover': { background: '#EAEDFF' },
};

/**
 * Lets the user receive/pay only part of a document: a 1,000 invoice paid 200 now.
 *
 * The document total and what earlier payments already covered are shown as read-only
 * tiles; ticking the checkbox turns the payment into a partial one, where the entered
 * amount becomes the amount of this receipt/payment and the rest stays open on the
 * document.
 */
const PaymentPartialAmountPanel = ({
    t,
    documentTotal,
    alreadyPaid = 0,
    amount,
    isPartial,
    onPartialToggle,
    onAmountChange,
    disabled = false,
    style,
}) => {
    const total = round2(toNumber(documentTotal));
    if (total <= 0) return null;

    const paidBefore = round2(Math.max(0, toNumber(alreadyPaid)));
    const openBalance = computeOpenBalance(total, paidBefore);
    const payingNow = round2(toNumber(amount));
    const exceedsOpenBalance = payingNow - openBalance > 0.01;
    const remainingAfter = round2(openBalance - payingNow);

    const applyPercent = (percent) => onAmountChange(round2((openBalance * percent) / 100));

    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '8px',
                padding: '8px 12px',
                marginBottom: '8px',
                borderRadius: '8px',
                background: 'rgba(255, 247, 232, 0.75)',
                border: '1px solid #F9AA2A',
                ...style,
            }}
        >
            <Tooltip title={t('PartialPaymentTooltip')} placement="top">
                <label
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '2px',
                        fontSize: '13px',
                        fontWeight: 700,
                        color: '#9A6100',
                        cursor: disabled ? 'default' : 'pointer',
                    }}
                >
                    <Checkbox
                        size="small"
                        checked={!!isPartial}
                        disabled={disabled}
                        onChange={(e) => onPartialToggle(e.target.checked)}
                        sx={{ padding: '4px', color: '#F9AA2A', '&.Mui-checked': { color: '#F9AA2A' } }}
                    />
                    {t('PartialPayment')}
                </label>
            </Tooltip>

            <SummaryTile label={t('DocumentAmount')} value={total} />
            {paidBefore > 0 && (
                <SummaryTile label={t('AlreadyPaidOnDocument')} value={paidBefore} tone="deduction" />
            )}
            <SummaryTile
                label={t('OpenBalance')}
                value={openBalance}
                tone="primary"
                tooltip={t('OpenBalanceTooltip')}
            />

            {isPartial && (
                <>
                    <div style={{ width: '1px', alignSelf: 'stretch', background: '#F9AA2A', margin: '0 4px' }} />

                    <AmountInput
                        label={t('PartialPaymentAmount')}
                        value={amount}
                        onChange={onAmountChange}
                        disabled={disabled}
                    />

                    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                        {PERCENT_SHORTCUTS.map((percent) => (
                            <Button
                                key={percent}
                                onClick={() => applyPercent(percent)}
                                disabled={disabled}
                                sx={shortcutButtonSx}
                            >
                                {`${percent}%`}
                            </Button>
                        ))}
                        <Tooltip title={t('FullOpenBalanceTooltip')} placement="top">
                            <span>
                                <Button
                                    onClick={() => onAmountChange(openBalance)}
                                    disabled={disabled}
                                    sx={shortcutButtonSx}
                                >
                                    {t('FullOpenBalance')}
                                </Button>
                            </span>
                        </Tooltip>
                    </div>

                    {payingNow > 0 && !exceedsOpenBalance && (
                        <SummaryTile
                            label={t('RemainingAfterThisPayment')}
                            value={remainingAfter}
                            tone="deduction"
                            tooltip={t('RemainingAfterThisPaymentTooltip')}
                        />
                    )}

                    {exceedsOpenBalance && (
                        <span style={{ fontSize: '12px', fontWeight: 600, color: '#D32F2F' }}>
                            {t('PartialAmountExceedsOpenBalance')}
                        </span>
                    )}
                </>
            )}
        </div>
    );
};

export default PaymentPartialAmountPanel;
